import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/dashboard/admin-sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Search, Loader2, CreditCard, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { formatPlatformCurrencyExact } from "@/lib/currency";

interface WithdrawalWithInvestor {
  _id: string;
  investorId: string;
  portfolioId: string;
  investmentDate: string;
  withdrawalDate: string;
  amount: string;
  payout: string;
  irr: string;
  investor?: {
    firstName: string;
    lastName: string;
    clientId: string;
  };
  product?: string;
}

export default function AdminWithdrawalsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedInvestorId, setSelectedInvestorId] = useState("");
  const [investmentDate, setInvestmentDate] = useState("");
  const [withdrawalDate, setWithdrawalDate] = useState("");
  const [amount, setAmount] = useState("");
  const [payout, setPayout] = useState("");
  const [irr, setIrr] = useState("");

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: withdrawals, isLoading } = useQuery<WithdrawalWithInvestor[]>({
    queryKey: ["/api/admin/withdrawals"],
  });

  const { data: investors } = useQuery<any[]>({
    queryKey: ["/api/admin/investors"],
  });

  const { data: portfolios } = useQuery<any[]>({
    queryKey: ["/api/admin/portfolios"],
  });

  const createWithdrawalMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/withdrawals", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/withdrawals"] });
      toast({
        title: "Success",
        description: "Withdrawal entry added successfully",
      });
      setIsAddOpen(false);
      resetForm();
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to Add Withdrawal",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setSelectedInvestorId("");
    setInvestmentDate("");
    setWithdrawalDate("");
    setAmount("");
    setPayout("");
    setIrr("");
  };

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvestorId) {
      toast({ title: "Error", description: "Please select an investor", variant: "destructive" });
      return;
    }
    if (!investmentDate || !withdrawalDate) {
      toast({ title: "Error", description: "Please select both investment and withdrawal dates", variant: "destructive" });
      return;
    }
    if (!amount || !payout) {
      toast({ title: "Error", description: "Please enter amount and payout", variant: "destructive" });
      return;
    }

    const portfolio = portfolios?.find(p => p.investorId === selectedInvestorId);
    createWithdrawalMutation.mutate({
      investorId: selectedInvestorId,
      portfolioId: portfolio?._id || portfolio?.id,
      investmentDate,
      withdrawalDate,
      amount,
      payout,
      irr: irr || "0",
    });
  };

  const enrichedWithdrawals = withdrawals?.map(w => {
    const investor = investors?.find(i => (i.id || i._id) === w.investorId);
    const portfolio = portfolios?.find(p => (p._id || p.id) === w.portfolioId);
    return {
      ...w,
      investor: investor ? {
        firstName: investor.firstName,
        lastName: investor.lastName,
        clientId: investor.clientId
      } : undefined,
      product: portfolio?.fundName || "Velocity Fund"
    };
  });

  const filteredWithdrawals = enrichedWithdrawals?.filter(w => {
    const searchStr = `${w.investor?.firstName} ${w.investor?.lastName} ${w.investor?.clientId} ${w.product}`.toLowerCase();
    return searchStr.includes(searchQuery.toLowerCase());
  }) || [];

  const totalAmountB = filteredWithdrawals.reduce((sum, w) => sum + parseInt(w.amount || "0"), 0);
  const totalPayoutSum = filteredWithdrawals.reduce((sum, w) => sum + parseInt(w.payout || "0"), 0);

  const formatCurrency = (val: string | number) =>
    formatPlatformCurrencyExact(typeof val === "string" ? parseInt(val) : val);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AdminSidebar />
        <main className="flex-1 overflow-auto">
          <header className="flex h-16 items-center justify-between border-b px-6 sticky top-0 bg-background/80 backdrop-blur-md z-10">
            <div className="flex items-center gap-4">
              <SidebarTrigger />
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-red-500" />
                <h1 className="text-xl font-semibold">Withdrawal B</h1>
              </div>
            </div>
            <Button onClick={() => setIsAddOpen(true)} className="gap-2 bg-red-600 hover:bg-red-700 text-white">
              <Plus className="w-4 h-4" />
              Add Withdrawal Entry
            </Button>
          </header>

          <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
            {/* Summary Card */}
            <Card className="bg-red-500/5 border-red-500/20">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                    <div className="text-center md:text-left">
                      <p className="text-xs text-muted-foreground uppercase tracking-widest font-semibold mb-1">Total Amount B (Withdrawn)</p>
                      <h2 className="text-4xl font-extrabold text-red-500 leading-none tracking-tight">{formatCurrency(totalAmountB)}</h2>
                    </div>
                    <div className="h-12 w-px bg-red-500/20 hidden md:block" />
                    <div className="text-center md:text-left opacity-80">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-semibold mb-1">Total Payout (Cash Out)</p>
                      <p className="text-2xl font-bold">{formatCurrency(totalPayoutSum)}</p>
                    </div>
                  </div>
                  <div className="text-right hidden lg:block bg-background/50 px-4 py-2 rounded-lg border border-red-500/10">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">Total Withdrawals</p>
                    <p className="text-2xl font-black text-red-500">{filteredWithdrawals.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
              <div className="relative w-full max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by investor, ID or product..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left py-4 px-4 font-semibold text-muted-foreground">Date of Investment</th>
                        <th className="text-left py-4 px-4 font-semibold text-muted-foreground">Date of withdraw</th>
                        <th className="text-left py-4 px-4 font-semibold text-muted-foreground">Name of Investor</th>
                        <th className="text-left py-4 px-4 font-semibold text-muted-foreground">Investor ID</th>
                        <th className="text-left py-4 px-4 font-semibold text-muted-foreground">Product</th>
                        <th className="text-right py-4 px-4 font-semibold text-muted-foreground">Amount (INR)</th>
                        <th className="text-right py-4 px-4 font-semibold text-muted-foreground">Payout (INR)</th>
                        <th className="text-right py-4 px-4 font-semibold text-muted-foreground">IRR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={8} className="py-20 text-center">
                            <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                            <p className="mt-2 text-muted-foreground">Loading withdrawals...</p>
                          </td>
                        </tr>
                      ) : filteredWithdrawals.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="py-20 text-center text-muted-foreground">
                            No withdrawals found.
                          </td>
                        </tr>
                      ) : (
                        filteredWithdrawals.map((w) => (
                          <tr key={w._id} className="border-b hover:bg-muted/30 transition-colors">
                            <td className="py-4 px-4 whitespace-nowrap">
                              {new Date(w.investmentDate).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "2-digit",
                              })}
                            </td>
                            <td className="py-4 px-4 whitespace-nowrap text-red-500 font-medium">
                              {new Date(w.withdrawalDate).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "2-digit",
                              })}
                            </td>
                            <td className="py-4 px-4 font-medium uppercase text-xs">
                              {w.investor ? `${w.investor.firstName} ${w.investor.lastName}` : "Unknown"}
                            </td>
                            <td className="py-4 px-4 text-muted-foreground">
                              {w.investor?.clientId || "-"}
                            </td>
                            <td className="py-4 px-4">
                              <Badge variant="outline" className="font-normal text-[10px] uppercase tracking-tight">
                                {w.product}
                              </Badge>
                            </td>
                            <td className="py-4 px-4 text-right">
                              {formatCurrency(parseInt(w.amount))}
                            </td>
                            <td className="py-4 px-4 text-right font-semibold text-accent">
                              {formatCurrency(parseInt(w.payout))}
                            </td>
                            <td className="py-4 px-4 text-right">
                              {w.irr}%
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>

        {/* Add Withdrawal Entry Dialog */}
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add Withdrawal Entry</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddSubmit} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Select Investor</Label>
                <Select value={selectedInvestorId} onValueChange={setSelectedInvestorId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Investor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {investors?.map((inv: any) => (
                      <SelectItem key={inv._id || inv.id} value={inv._id || inv.id}>
                        {inv.firstName} {inv.lastName} ({inv.clientId || "No ID"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date of Investment</Label>
                  <Input
                    type="date"
                    value={investmentDate}
                    onChange={(e) => setInvestmentDate(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date of Withdrawal</Label>
                  <Input
                    type="date"
                    value={withdrawalDate}
                    onChange={(e) => setWithdrawalDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount (INR)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 500000"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Payout (INR)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 550000"
                    value={payout}
                    onChange={(e) => setPayout(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>IRR (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 12.5"
                  value={irr}
                  onChange={(e) => setIrr(e.target.value)}
                />
              </div>

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setIsAddOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createWithdrawalMutation.isPending} className="bg-red-600 hover:bg-red-700 text-white">
                  {createWithdrawalMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Withdrawal Entry"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </SidebarProvider>
  );
}
